import type { Dayjs } from "dayjs";
import { type CreateScenarioPayload } from "../../data/types";

export interface ChartDataPoint {
  timestamp: string;
  fullDate?: string;
  scenario: number;
  baseline: number;
}

/**
 * Generates a unique cache key based on the scenario parameters
 */
export const getScenarioCacheKey = (payload: CreateScenarioPayload): string => {
  return JSON.stringify({ payload });
};

/**
 * Helper to clamp values or format units if needed for the UI
 */
export const formatParamLabel = (value: number, unit: string = "") => {
  return `${value}${unit}`;
};

/**
 * Formats a date range for the simulation API.
 * Ensures the user's local selection is treated as the UTC start date.
 */
export const formatSimulationDates = (selectedDate: Date) => {
  // 1. Create a new Date by extracting year, month, and day
  // This ignores the local timezone offset.
  const start = new Date(
    Date.UTC(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      0,
      0,
      0,
      0,
    ),
  );

  // 2. Set End Date to exactly +1 day from the start
  const end = new Date(start);
  end.setUTCDate(start.getUTCDate() + 1);

  // 3. Format to YYYY-MM-DDTHH:mm:ssZ
  const format = (d: Date) => d.toISOString().split(".")[0] + "Z";

  return {
    start_datetime: format(start),
    end_datetime: format(end),
  };
};

const isoFormat = (d: Date) => d.toISOString().split(".")[0] + "Z";

export const toISOStartOfDay = (d: Dayjs): string =>
    isoFormat(new Date(Date.UTC(d.year(), d.month(), d.date(), 0, 0, 0)));

export const toISOEndOfDay = (d: Dayjs): string =>
    isoFormat(new Date(Date.UTC(d.year(), d.month(), d.date() + 1, 0, 0, 0)));

// This filters the data to find the timestamps that are exactly on the 3-hour mark
export const getThreeHourTicks = (data: ChartDataPoint[]): string[] => {
  return data
    .filter((d) => {
      // Logic: Split by 'T', take the time part '00:00:00', check the hour
      const timePart = d.timestamp.split("T")[1];
      if (!timePart) return false;
      const hour = parseInt(timePart.split(":")[0]);
      const minute = parseInt(timePart.split(":")[1]);
      return minute === 0 && hour % 3 === 0;
    })
    .map((d) => d.timestamp);
};
